import React, { useState, useEffect, useCallback, useRef } from 'react';
import './UGTeacherReview.css';
import collegeConfig from '../../config/collegeConfig';
import {
  FaTachometerAlt, FaBookMedical, FaUserMd, FaPenFancy, FaExchangeAlt,
  FaStethoscope, FaHouseUser, FaShieldVirus, FaUniversity, FaChalkboardTeacher,
  FaClipboardCheck, FaUserTie, FaBriefcaseMedical, FaUserCog, FaSignOutAlt,
  FaBars, FaTimes, FaArrowRight, FaUser, FaUsers, FaClipboardList, FaBell,
  FaCertificate
} from 'react-icons/fa';
import ELogbookReview from './ELogbookReview';
import UGCertificateGeneration from './UGCertificateGeneration';
import ProgressModuleReview from './ProgressModuleReview';
import {
  ReflectionsReview, ElectivesReview, EceReview, FapReview,
  ConferenceReview, InternshipActivityReview
} from './SimpleLogReviewPanels';
import AssessmentRequestReview from './AssessmentRequestReview';
import ManageTeacherAccount from '../TeacherDashboard/ManageTeacherAccount';

// Colors reuse PG TeacherDashboard's own per-item accent-color pattern
// (sidebar1MenuItems / action-icon backgrounds in TeacherDashboard.js) so
// the two sidebars feel like the same product, just different menus.
const NAV_ITEMS = [
  { id: 'dashboard', title: 'Dashboard', icon: <FaTachometerAlt />, color: '#1568f6' },
  { id: 'elogbook', title: 'E-Logbook Review', icon: <FaBookMedical />, color: '#f32e2e' },
  { id: 'aetcom', title: 'AETCOM Review', icon: <FaUserMd />, color: '#9B59B6' },
  { id: 'reflections', title: 'Reflections Review', icon: <FaPenFancy />, color: '#16A085' },
  { id: 'electives', title: 'Electives Review', icon: <FaExchangeAlt />, color: '#3498DB' },
  { id: 'ece', title: 'ECE Review', icon: <FaStethoscope />, color: '#E67E22' },
  { id: 'fap', title: 'FAP Review', icon: <FaHouseUser />, color: '#27AE60' },
  { id: 'pandemic', title: 'Pandemic Module Review', icon: <FaShieldVirus />, color: '#c62828' },
  { id: 'foundation-course', title: 'Foundation Course Review', icon: <FaUniversity />, color: '#5C6BC0' },
  { id: 'conference', title: 'Conference Review', icon: <FaChalkboardTeacher />, color: '#2E86C1' },
  { id: 'doap', title: 'DOAP Review', icon: <FaClipboardCheck />, color: '#8E44AD' },
  { id: 'professionalism', title: 'Professionalism Review', icon: <FaUserTie />, color: '#F39C12' },
  { id: 'internship', title: 'Internship Review', icon: <FaBriefcaseMedical />, color: '#E74C3C' },
  { id: 'certificates', title: 'E-Logbook Certificates', icon: <FaCertificate />, color: '#c62828' }
];

const UGTeacherReview = ({ programSwitcher }) => {
  const [activeView, setActiveView] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [user, setUser] = useState(null);
  const [assignedSubjects, setAssignedSubjects] = useState([]);
  const [ugBatches, setUgBatches] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const profileMenuRef = useRef(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) setUser(JSON.parse(storedUser));
  }, []);

  // Close profile menu when clicking outside — same pattern as
  // TeacherDashboard.js / UGStudentDashboard.js's own profile dropdowns.
  useEffect(() => {
    if (!showProfileMenu) return;
    const handleOutsideClick = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [showProfileMenu]);

  const fetchTeacherInfo = useCallback(async () => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser) return;
    const parsedUser = JSON.parse(storedUser);
    const userId = parsedUser.id || parsedUser._id;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/teachers/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setAssignedSubjects(data.teacher?.assignedUGSubjects || []);
    } catch (err) {
      console.error('Error fetching UG teacher info:', err);
    }
  }, []);

  const fetchUGBatches = useCallback(async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/batches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setUgBatches(data.batches || []);
    } catch (err) {
      console.error('Error fetching UG batches:', err);
    }
  }, []);

  const fetchPendingCount = useCallback(async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/competency-logs/pending-review`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setPendingCount((data.logs || []).length);
    } catch (err) {
      console.error('Error fetching UG pending count:', err);
    }
  }, []);

  useEffect(() => {
    fetchTeacherInfo();
    fetchPendingCount();
    fetchUGBatches();
  }, [fetchTeacherInfo, fetchPendingCount, fetchUGBatches]);

  // Notifications — same /notices/dashboard/teachers feed and read-tracking
  // mechanics as PG's TeacherDashboard.js, just ported onto this dashboard.
  useEffect(() => {
    if (!user) return;
    const fetchNotifications = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${collegeConfig.api.baseURL}/notices/dashboard/teachers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setNotifications(data.notices || []);
          const userId = user.id || user._id;
          const unread = (data.notices || []).filter(notice => {
            if (!notice.readBy || notice.readBy.length === 0) return true;
            return !notice.readBy.some(read =>
              (read.userId === userId || read.userId?._id === userId || read.userId?.toString() === userId.toString())
            );
          }).length;
          setUnreadNotifications(unread);
        }
      } catch (error) {
        console.error('Error fetching UG teacher notifications:', error);
      }
    };
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const isNotificationRead = (notification) => {
    const userId = user.id || user._id;
    return notification.readBy?.some(read =>
      (read.userId === userId || read.userId?._id === userId || read.userId?.toString() === userId.toString())
    );
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      const userId = user.id || user._id;
      const notification = notifications.find(n => n._id === notificationId);
      const isAlreadyRead = notification?.readBy?.some(read =>
        (read.userId === userId || read.userId?._id === userId || read.userId?.toString() === userId.toString())
      );
      if (isAlreadyRead) return;

      const response = await fetch(`${collegeConfig.api.baseURL}/notices/${notificationId}/read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        setNotifications(prev =>
          prev.map(n => n._id === notificationId
            ? { ...n, readBy: [...(n.readBy || []), { userId, readAt: new Date() }] }
            : n
          )
        );
        setUnreadNotifications(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error marking UG teacher notification as read:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    markNotificationAsRead(notification._id);
    setSelectedNotification(notification);
    setShowNotifications(false);
  };

  const closeNotificationPopup = () => setSelectedNotification(null);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.reload();
  };

  const handleNavClick = (id) => {
    setActiveView(id);
    setSidebarOpen(false);
    setShowProfileMenu(false);
    if (id === 'dashboard') fetchPendingCount();
  };

  const renderContent = () => {
    switch (activeView) {
      case 'dashboard':
        return (
          <>
            <div className="ugtr-dash-welcome">
              <h1 className="ugtr-welcome-title">Welcome back, {user?.profile?.firstName}!</h1>
              <p className="ugtr-welcome-subtitle">Review and verify your UG students' submissions.</p>
            </div>

            <div className="ugtr-stat-cards">
              <div className="ugtr-stat-card">
                <div className="ugtr-stat-icon" style={{ background: '#4A90E2' }}><FaUsers /></div>
                <div>
                  <div className="ugtr-stat-value">{assignedSubjects.length}</div>
                  <div className="ugtr-stat-label">Assigned UG Subjects</div>
                </div>
              </div>
              <div className="ugtr-stat-card">
                <div className="ugtr-stat-icon" style={{ background: '#E74C3C' }}><FaClipboardList /></div>
                <div>
                  <div className="ugtr-stat-value">{pendingCount}</div>
                  <div className="ugtr-stat-label">Pending E-Logbook Entries</div>
                </div>
              </div>
            </div>

            <h3 className="ugtr-quick-actions-title">Quick Actions</h3>
            <div className="ugtr-action-cards">
              {NAV_ITEMS.filter(i => i.id !== 'dashboard').map(item => (
                <div key={item.id} className="ugtr-action-card" onClick={() => handleNavClick(item.id)}>
                  <div className="ugtr-action-icon" style={{ background: item.color }}>{item.icon}</div>
                  <div>
                    <h4>{item.title}</h4>
                    <p>Review student submissions</p>
                  </div>
                  <FaArrowRight style={{ marginLeft: 'auto', color: '#8792a6' }} />
                </div>
              ))}
            </div>
          </>
        );
      case 'elogbook':
        return <ELogbookReview assignedSubjects={assignedSubjects} ugBatches={ugBatches} />;
      case 'manage-account':
        return <ManageTeacherAccount user={user} />;
      case 'aetcom':
        return <ProgressModuleReview title="AETCOM Review" apiPath="aetcom" moduleKeyField="moduleNumber" ugBatches={ugBatches} />;
      case 'reflections':
        return <ReflectionsReview ugBatches={ugBatches} />;
      case 'electives':
        return <ElectivesReview ugBatches={ugBatches} />;
      case 'ece':
        return <EceReview ugBatches={ugBatches} />;
      case 'fap':
        return <FapReview ugBatches={ugBatches} />;
      case 'pandemic':
        return <ProgressModuleReview title="Pandemic Module Review" apiPath="pandemic" moduleKeyField="moduleNumber" ugBatches={ugBatches} />;
      case 'foundation-course':
        return (
          <ProgressModuleReview
            title="Foundation Course Review"
            apiPath="foundation-course"
            moduleKeyField="moduleId"
            showAttendance
            ugBatches={ugBatches}
          />
        );
      case 'conference':
        return <ConferenceReview ugBatches={ugBatches} />;
      case 'doap':
        return (
          <AssessmentRequestReview
            title="DOAP Review"
            apiPath="doap"
            listKey="sessions"
            ugBatches={ugBatches}
            domains={['Knowledge (indications/anatomy/technique)', 'Consent & Preparation', 'Technical Ability', 'Communication & Post-procedure Care']}
            hasGlobalRating
            renderRequestBody={(item) => (
              <>
                <div className="ugtr-info-item">
                  <span className="ugtr-info-label">Procedure</span>
                  <span className="ugtr-info-value">{item.procedure}</span>
                </div>
                {item.proposedDate && (
                  <div className="ugtr-info-item">
                    <span className="ugtr-info-label">Proposed Date</span>
                    <span className="ugtr-info-value">{new Date(item.proposedDate).toLocaleDateString()}</span>
                  </div>
                )}
                {item.venueWard && (
                  <div className="ugtr-info-item">
                    <span className="ugtr-info-label">Venue/Ward</span>
                    <span className="ugtr-info-value">{item.venueWard}</span>
                  </div>
                )}
                {item.selectedTeacherIds?.length > 0 && (
                  <div className="ugtr-info-item">
                    <span className="ugtr-info-label">Selected Faculty</span>
                    <span className="ugtr-info-value">
                      {item.selectedTeacherIds.map(t => `${t.profile?.firstName} ${t.profile?.lastName}`).join(', ')}
                    </span>
                  </div>
                )}
              </>
            )}
          />
        );
      case 'professionalism':
        return (
          <AssessmentRequestReview
            title="Professionalism Review"
            apiPath="professionalism"
            listKey="assessments"
            ugBatches={ugBatches}
            domains={['Punctuality & Reliability', 'Respect for Patients/Colleagues', 'Integrity & Honesty', 'Responsibility & Accountability', 'Empathy & Communication']}
            hasGlobalRating={false}
            renderRequestBody={(item) => (
              <>
                <div className="ugtr-info-item">
                  <span className="ugtr-info-label">Posting/Department</span>
                  <span className="ugtr-info-value">{item.postingDepartment}</span>
                </div>
                {item.selectedTeacherIds?.length > 0 && (
                  <div className="ugtr-info-item">
                    <span className="ugtr-info-label">Selected Faculty</span>
                    <span className="ugtr-info-value">
                      {item.selectedTeacherIds.map(t => `${t.profile?.firstName} ${t.profile?.lastName}`).join(', ')}
                    </span>
                  </div>
                )}
                {item.noteToFaculty && (
                  <div className="ugtr-info-item ugtr-entry-body-full">
                    <span className="ugtr-info-label">Note to Faculty</span>
                    <span className="ugtr-info-value">{item.noteToFaculty}</span>
                  </div>
                )}
              </>
            )}
          />
        );
      case 'internship':
        return <InternshipActivityReview ugBatches={ugBatches} />;
      case 'certificates':
        return <UGCertificateGeneration />;
      default:
        return null;
    }
  };

  if (!user) return null;

  const pageTitle = NAV_ITEMS.find(i => i.id === activeView)?.title
    || (activeView === 'manage-account' ? 'Manage My Account' : 'Dashboard');

  return (
    <div className="ugtr-shell">
      <div className={`ugtr-sidebar ${sidebarOpen ? 'ugtr-sidebar-open' : ''}`}>
        <div className="ugtr-sidebar-header">
          <div className="ugtr-sidebar-brand">
            <img
              src={collegeConfig.logo}
              alt={`${collegeConfig.shortName} Logo`}
              className="ugtr-sidebar-logo"
            />
            <div className="ugtr-sidebar-logo-text">
              <h2>{collegeConfig.shortName}</h2>
              <p>{collegeConfig.appName}</p>
            </div>
          </div>
        </div>
        {NAV_ITEMS.map(item => (
          <div
            key={item.id}
            className={`ugtr-nav-item ${activeView === item.id ? 'ugtr-nav-active' : ''}`}
            onClick={() => handleNavClick(item.id)}
          >
            <span className="ugtr-nav-icon" style={{ color: item.color }}>{item.icon}</span>
            <span>{item.title}</span>
          </div>
        ))}
        <div className="ugtr-sidebar-footer">
          <div
            className={`ugtr-nav-item ${activeView === 'manage-account' ? 'ugtr-nav-active' : ''}`}
            onClick={() => handleNavClick('manage-account')}
          >
            <span className="ugtr-nav-icon" style={{ color: '#9B59B6' }}><FaUserCog /></span>
            <span>Manage Account</span>
          </div>
          <div className="ugtr-nav-item ugtr-logout-item" onClick={handleLogout}>
            <span className="ugtr-nav-icon"><FaSignOutAlt /></span>
            <span>Logout</span>
          </div>
        </div>
      </div>

      <div className="ugtr-main">
        <header className="ugtr-header-bar">
          <div className="ugtr-header-left">
            <button className="ugtr-mobile-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
              {sidebarOpen ? <FaTimes /> : <FaBars />}
            </button>
            <h2 className="ugtr-page-title">{pageTitle}</h2>
          </div>

          <div className="ugtr-header-right">
            {programSwitcher}
            <div className="notification-wrapper" style={{ position: 'relative', marginRight: '20px' }}>
              <div
                className="notification-icon"
                onClick={() => setShowNotifications(!showNotifications)}
                style={{ cursor: 'pointer', fontSize: '20px', color: '#666', position: 'relative' }}
              >
                <FaBell />
                {unreadNotifications > 0 && (
                  <span style={{
                    position: 'absolute', top: '-8px', right: '-8px', background: '#E74C3C',
                    color: 'white', borderRadius: '50%', width: '20px', height: '20px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '12px', fontWeight: 'bold'
                  }}>
                    {unreadNotifications}
                  </span>
                )}
              </div>

              {showNotifications && (
                <div style={{
                  position: 'absolute', top: '100%', right: 0, background: 'white',
                  borderRadius: '12px', boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
                  minWidth: '350px', maxWidth: 'calc(100vw - 40px)', maxHeight: '400px',
                  overflow: 'hidden', zIndex: 1000, marginTop: '10px'
                }}>
                  <div style={{
                    padding: '15px 20px', borderBottom: '1px solid #e9ecef',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                  }}>
                    <h3 style={{ margin: 0, fontSize: '16px' }}>Notifications</h3>
                    {unreadNotifications > 0 && (
                      <span style={{ fontSize: '12px', color: '#666' }}>{unreadNotifications} unread</span>
                    )}
                  </div>
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {notifications.length > 0 ? (
                      notifications.slice(0, 5).map((notification) => (
                        <div
                          key={notification._id}
                          style={{
                            padding: '15px 20px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer',
                            background: !isNotificationRead(notification) ? '#f8f9fa' : 'white',
                            transition: 'background 0.2s'
                          }}
                          onClick={() => handleNotificationClick(notification)}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                          onMouseLeave={(e) => e.currentTarget.style.background =
                            !isNotificationRead(notification) ? '#f8f9fa' : 'white'
                          }
                        >
                          <h4 style={{ margin: '0 0 5px', fontSize: '14px', fontWeight: '600' }}>
                            {notification.title}
                          </h4>
                          <p style={{ margin: '0 0 5px', fontSize: '13px', color: '#666' }}>
                            {notification.content.substring(0, 100)}...
                          </p>
                          <span style={{ fontSize: '11px', color: '#999' }}>
                            {new Date(notification.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: '30px', textAlign: 'center', color: '#999' }}>
                        <p>No notifications</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="ugtr-profile-section" ref={profileMenuRef} onClick={() => setShowProfileMenu(!showProfileMenu)}>
              <div className="ugtr-profile-avatar"><FaUser /></div>
              <div className="ugtr-profile-info">
                <span className="ugtr-profile-name">{user.profile?.firstName} {user.profile?.lastName}</span>
                <span className="ugtr-profile-role">UG Teacher</span>
              </div>
              <span className="ugtr-dropdown-arrow">▼</span>

              {showProfileMenu && (
                <div className="ugtr-profile-dropdown">
                  <div className="ugtr-dropdown-item" onClick={() => handleNavClick('manage-account')}>
                    <FaUserCog /> My Account
                  </div>
                  <div className="ugtr-dropdown-divider"></div>
                  <div className="ugtr-dropdown-item ugtr-dropdown-item-logout" onClick={handleLogout}>
                    <FaSignOutAlt /> Logout
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {selectedNotification && (
          <div
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0, 0, 0, 0.6)', display: 'flex', alignItems: 'center',
              justifyContent: 'center', zIndex: 2000, padding: '20px'
            }}
            onClick={closeNotificationPopup}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'white', borderRadius: '16px', padding: '30px', maxWidth: '600px',
                width: '100%', maxHeight: '80vh', overflow: 'auto',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)', position: 'relative'
              }}
            >
              <button
                onClick={closeNotificationPopup}
                style={{
                  position: 'absolute', top: '20px', right: '20px', background: 'none', border: 'none',
                  fontSize: '24px', cursor: 'pointer', color: '#666', padding: '5px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
              >
                <FaTimes />
              </button>

              <div style={{ marginBottom: '15px' }}>
                <span style={{
                  display: 'inline-block', padding: '4px 12px',
                  background: selectedNotification.priority === 'high' ? '#E74C3C' :
                             selectedNotification.priority === 'medium' ? '#F39C12' : '#3498DB',
                  color: 'white', borderRadius: '12px', fontSize: '12px', fontWeight: '600',
                  textTransform: 'uppercase'
                }}>
                  {selectedNotification.priority} Priority
                </span>
                <span style={{
                  display: 'inline-block', marginLeft: '10px', padding: '4px 12px',
                  background: '#9B59B6', color: 'white', borderRadius: '12px',
                  fontSize: '12px', fontWeight: '600', textTransform: 'capitalize'
                }}>
                  {selectedNotification.type}
                </span>
              </div>

              <h2 style={{ margin: '0 0 15px', fontSize: '24px', color: '#333', paddingRight: '40px' }}>
                {selectedNotification.title}
              </h2>

              <div style={{
                display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px',
                paddingBottom: '20px', borderBottom: '1px solid #e9ecef'
              }}>
                <span style={{ fontSize: '13px', color: '#666' }}>
                  Posted: {new Date(selectedNotification.publishDate || selectedNotification.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric', month: 'long', day: 'numeric'
                  })}
                </span>
                {selectedNotification.expiryDate && (
                  <span style={{ fontSize: '13px', color: '#E74C3C' }}>
                    Expires: {new Date(selectedNotification.expiryDate).toLocaleDateString('en-US', {
                      year: 'numeric', month: 'long', day: 'numeric'
                    })}
                  </span>
                )}
              </div>

              <div style={{ fontSize: '15px', lineHeight: '1.8', color: '#555', whiteSpace: 'pre-wrap' }}>
                {selectedNotification.content}
              </div>

              {selectedNotification.attachments && selectedNotification.attachments.length > 0 && (
                <div style={{ marginTop: '25px' }}>
                  <h4 style={{ margin: '0 0 10px', fontSize: '16px', color: '#333' }}>Attachments</h4>
                  {selectedNotification.attachments.map((attachment, index) => (
                    <a
                      key={index}
                      href={attachment.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'block', padding: '10px 15px', background: '#f8f9fa', borderRadius: '8px',
                        marginBottom: '8px', color: '#3498DB', textDecoration: 'none', fontSize: '14px'
                      }}
                    >
                      📎 {attachment.fileName}
                    </a>
                  ))}
                </div>
              )}

              <div style={{
                marginTop: '25px', paddingTop: '20px', borderTop: '1px solid #e9ecef',
                display: 'flex', justifyContent: 'flex-end', alignItems: 'center'
              }}>
                <button
                  onClick={closeNotificationPopup}
                  style={{
                    padding: '10px 24px', background: '#27AE60', color: 'white', border: 'none',
                    borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="ugtr-page-content">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default UGTeacherReview;
